import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, FileText, Plus, Trash2, Save, Send, RotateCcw, CheckCircle2 } from "lucide-react";

const DEFAULT_PAY_NOTE =
  "Los pagos se realizarán mediante transferencia bancaria a la cuenta a notificar por Grabarte en un plazo máximo de 30 días.";

function eur(n) {
  const s = (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} €`;
}
function esc(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
  catch { return ""; }
}
const pad2 = (n) => String(n).padStart(2, "0");
const STATUS_LABEL = { draft: "Borrador", sent: "Enviada", accepted: "Aceptada" };

export default function PropuestaCliente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [logo, setLogo] = useState("");

  const baseJson = useRef(null);
  const [versions, setVersions] = useState([]);
  const [currentVid, setCurrentVid] = useState(null);
  const [currentMeta, setCurrentMeta] = useState(null); // {version, status, sent_at}

  const [header, setHeader] = useState({
    id_ref: "", project_name: "", contact: "", by: "", client: "", provider: "", date: "", subtitle: "",
  });
  const [chapters, setChapters] = useState([]); // [{name, subs:[{name, price, concepts}]}]
  const [iva, setIva] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [pays, setPays] = useState([]);
  const [payNote, setPayNote] = useState(DEFAULT_PAY_NOTE);

  const applyFromJson = (d) => {
    setHeader({
      id_ref: d.id_ref || "", project_name: d.project_name || "", contact: d.contact || "",
      by: d.by || "", client: d.client || "", provider: d.provider || "",
      date: d.date || "", subtitle: `Propuesta económica para ${d.client || "el cliente"}`,
    });
    setChapters((d.chapters || []).map((ch) => ({
      name: ch.name || "",
      subs: (ch.subs || []).map((s) => ({ name: s.name || "", price: Number(s.price) || 0, concepts: (s.concepts || []).join("\n") })),
    })));
    setIva(false); setShowPay(false); setPays([]); setPayNote(DEFAULT_PAY_NOTE);
  };

  const applyContent = (c = {}) => {
    setHeader({
      id_ref: "", project_name: "", contact: "", by: "", client: "", provider: "", date: "", subtitle: "",
      ...(c.header || {}),
    });
    setChapters(c.chapters || []);
    setIva(!!c.iva); setShowPay(!!c.showPay); setPays(c.pays || []);
    setPayNote(c.payNote ?? DEFAULT_PAY_NOTE);
  };

  const loadVersion = async (vid) => {
    const res = await api.get(`/projects/${id}/proposal-versions/${vid}`);
    const v = res.data || {};
    applyContent(v.content || {});
    setCurrentVid(vid);
    setCurrentMeta({ version: v.version, status: v.status, sent_at: v.sent_at });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [jsonRes, versRes] = await Promise.all([
          api.get(`/projects/${id}/proposal-json`),
          api.get(`/projects/${id}/proposal-versions`),
        ]);
        if (!alive) return;
        const d = jsonRes.data || {};
        baseJson.current = d;
        setLogo(d.logo || "");
        const vers = versRes.data || [];
        setVersions(vers);
        if (vers.length) await loadVersion(vers[0].id);
        else { applyFromJson(d); setCurrentVid(null); setCurrentMeta(null); }
      } catch { if (alive) toast.error("No se pudo cargar la propuesta"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const chapTotal = (ch) => ch.subs.reduce((a, s) => a + (Number(s.price) || 0), 0);
  const total = useMemo(() => chapters.reduce((a, ch) => a + chapTotal(ch), 0), [chapters]);
  const ivaAmt = useMemo(() => Math.round(total * 0.21 * 100) / 100, [total]);

  // --- mutators ---
  const upHeader = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const upChapter = (ci, k, v) => setChapters((c) => c.map((ch, i) => i === ci ? { ...ch, [k]: v } : ch));
  const upSub = (ci, si, k, v) => setChapters((c) => c.map((ch, i) => i !== ci ? ch : ({
    ...ch, subs: ch.subs.map((s, j) => j === si ? { ...s, [k]: v } : s),
  })));
  const addSub = (ci) => setChapters((c) => c.map((ch, i) => i !== ci ? ch : ({ ...ch, subs: [...ch.subs, { name: "Nueva partida", price: 0, concepts: "" }] })));
  const delSub = (ci, si) => setChapters((c) => c.map((ch, i) => i !== ci ? ch : ({ ...ch, subs: ch.subs.filter((_, j) => j !== si) })));
  const addChapter = () => setChapters((c) => [...c, { name: "Nuevo capítulo", subs: [{ name: "Nueva partida", price: 0, concepts: "" }] }]);
  const delChapter = (ci) => setChapters((c) => c.filter((_, i) => i !== ci));

  const upPay = (i, k, v) => setPays((p) => p.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addPay = () => setPays((p) => [...p, { label: "Factura", pct: 0 }]);
  const delPay = (i) => setPays((p) => p.filter((_, j) => j !== i));
  const togglePay = (on) => {
    setShowPay(on);
    if (on && pays.length === 0) {
      setPays([{ label: "Primera factura — arranque de proyecto", pct: 50 }, { label: "Segunda factura — entrega final", pct: 50 }]);
    }
  };

  // --- version actions ---
  const buildContent = () => ({ header, chapters, iva, showPay, pays, payNote });

  const refreshVersions = async () => {
    const res = await api.get(`/projects/${id}/proposal-versions`);
    setVersions(res.data || []);
    return res.data || [];
  };

  const saveVersion = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/projects/${id}/proposal-versions`, { content: buildContent(), total });
      const nv = res.data;
      await refreshVersions();
      setCurrentVid(nv.id);
      setCurrentMeta({ version: nv.version, status: nv.status, sent_at: nv.sent_at });
      toast.success(`Versión v${nv.version} guardada`);
      return nv.id;
    } catch { toast.error("No se pudo guardar la versión"); return null; }
    finally { setSaving(false); }
  };

  const setStatus = async (newStatus) => {
    setMarking(true);
    try {
      let vid = currentVid;
      if (!vid) { vid = await saveVersion(); if (!vid) return; }
      const res = await api.post(`/projects/${id}/proposal-versions/${vid}/status`, { status: newStatus });
      setCurrentMeta({ version: res.data.version, status: res.data.status, sent_at: res.data.sent_at, accepted_at: res.data.accepted_at });
      await refreshVersions();
      if (newStatus === "accepted") {
        const c = res.data.created_invoices || 0;
        toast.success(c > 0 ? `Presupuesto aceptado · ${c} factura(s) a emitir creadas en el proyecto` : "Presupuesto aceptado (sin cronograma de pagos → no se generaron facturas)");
      } else {
        const rmv = res.data.removed_invoices || 0;
        const base = newStatus === "sent" ? "Marcada como enviada al cliente" : "Vuelta a borrador";
        toast.success(rmv > 0 ? `${base} · ${rmv} factura(s) pendientes eliminadas` : base);
      }
    } catch { toast.error("No se pudo actualizar el estado"); }
    finally { setMarking(false); }
  };

  const regenerateFromBudget = () => {
    if (baseJson.current) { applyFromJson(baseJson.current); setCurrentVid(null); setCurrentMeta(null); toast.info("Cargado desde el presupuesto actual (sin guardar)"); }
  };

  // --- build print HTML (must mirror PROPOSAL_PRINT_CSS classes on backend) ---
  const buildBodyHtml = () => {
    const rows = [];
    chapters.forEach((ch, i) => {
      rows.push(`<tr class="chapter"><td class="num">${pad2(i + 1)}</td><td class="name">${esc(ch.name)}</td><td class="price">${eur(chapTotal(ch))}</td></tr>`);
      ch.subs.forEach((s, j) => {
        const lines = (s.concepts || "").split("\n").map((x) => x.trim()).filter(Boolean);
        const ul = lines.length ? `<ul class="concepts">${lines.map((c) => `<li>${esc(c)}</li>`).join("")}</ul>` : "";
        rows.push(`<tr class="sub"><td class="num">${pad2(i + 1)}.${pad2(j + 1)}</td><td class="name">${esc(s.name)}${ul}</td><td class="price">${eur(s.price)}</td></tr>`);
      });
    });
    if (!rows.length) rows.push('<tr><td></td><td class="name" style="color:#94a3b8">Sin partidas.</td><td></td></tr>');

    const totalsHtml = iva
      ? `<div class="trow"><span>Base imponible</span><span>${eur(total)}</span></div><div class="trow"><span>IVA (21%)</span><span>${eur(ivaAmt)}</span></div><div class="trow total"><span>TOTAL</span><span>${eur(total + ivaAmt)}</span></div>`
      : `<div class="trow total"><span>TOTAL</span><span>${eur(total)}</span></div>`;

    const infoPairs = [["ID", `#${header.id_ref}`], ["Proyecto", header.project_name], ["Contacto", header.contact || "—"], ["By", header.by || "—"], ["Cliente", header.client || "—"], ["Proveedor", header.provider || "—"]];
    const infoHtml = infoPairs.map(([k, v]) => `<div class="info-item"><span class="k">${k}</span><span class="v">${esc(v)}</span></div>`).join("");

    let payHtml = "";
    if (showPay && pays.length) {
      const prows = pays.map((r) => `<tr><td class="name">${esc(r.label)}</td><td class="pct">${(Number(r.pct) || 0).toLocaleString("es-ES")} %</td><td class="amt">${eur((Number(r.pct) || 0) / 100 * total)}</td></tr>`).join("");
      payHtml = `<div class="section">Cronograma de pagos</div><div class="pay"><table><tbody>${prows}</tbody></table>` + (payNote.trim() ? `<div class="note">${esc(payNote.trim())}</div>` : "") + `</div>`;
    }

    return (
      `<div class="head"><img class="logo" src="__LOGO__" alt="Grabarte"/><div class="date">Presupuesto<br>${esc(header.date)}</div></div>` +
      `<div class="title">${esc(header.project_name)}</div>` +
      `<div class="subtitle">${esc(header.subtitle)}</div>` +
      `<div class="info">${infoHtml}</div>` +
      `<div class="section">Partidas presupuestarias</div>` +
      `<table><tbody>${rows.join("")}</tbody></table>` +
      `<div class="totals">${totalsHtml}</div>` + payHtml +
      `<div class="foot">${esc(header.provider || "")} · Presupuesto válido durante 30 días desde la fecha de emisión.<br>Los importes ${iva ? "incluyen" : "no incluyen"} IVA. Este documento tiene carácter de propuesta y no supone contrato hasta su aceptación.</div>`
    );
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const html = buildBodyHtml();
      const res = await api.post(`/projects/${id}/proposal-pdf`, { html, filename: `Presupuesto_${header.id_ref}_${header.project_name}` }, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "presupuesto.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) { toast.error("No se pudo generar el PDF"); } finally { setDownloading(false); }
  };

  const inp = "bg-transparent outline-none rounded px-1 -mx-1 focus:bg-amber-50 hover:bg-slate-50 transition-colors";

  return (
    <div className="min-h-screen bg-muted/40 pb-16" data-testid="propuesta-page">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(`/proyecto/${id}`)} data-testid="propuesta-back"><ArrowLeft className="h-4 w-4" /></Button>
          <div className="flex items-center gap-2 font-heading font-bold"><FileText className="h-4 w-4 text-primary" /> Propuesta para cliente</div>
          {currentMeta && (
            currentMeta.status === "accepted"
              ? <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 font-normal gap-1" data-testid="prop-status-badge"><CheckCircle2 className="h-3 w-3" /> Aceptada v{currentMeta.version}{currentMeta.accepted_at ? ` · ${fmtDate(currentMeta.accepted_at)}` : ""}</Badge>
              : currentMeta.status === "sent"
                ? <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 font-normal gap-1" data-testid="prop-status-badge"><Send className="h-3 w-3" /> Enviada v{currentMeta.version}{currentMeta.sent_at ? ` · ${fmtDate(currentMeta.sent_at)}` : ""}</Badge>
                : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-normal" data-testid="prop-status-badge">Borrador v{currentMeta.version}</Badge>
          )}
          {!currentMeta && !loading && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-normal" data-testid="prop-status-badge">Sin guardar</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {versions.length > 0 && (
            <Select value={currentVid || ""} onValueChange={(v) => loadVersion(v)}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="prop-version-select">
                <span className="truncate">{currentMeta ? `v${currentMeta.version} · ${STATUS_LABEL[currentMeta.status] || "Borrador"}` : "Versión"}</span>
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id} data-testid={`prop-version-opt-${v.version}`}>
                    v{v.version} · {v.status === "sent" ? `Enviada ${fmtDate(v.sent_at)}` : "Borrador"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="sm" className="gap-1.5 h-9" onClick={regenerateFromBudget} data-testid="prop-from-budget" title="Cargar desde el presupuesto actual"><RotateCcw className="h-3.5 w-3.5" /> Desde presupuesto</Button>
          <div className="flex items-center gap-2"><Label htmlFor="pay-toggle" className="text-sm text-muted-foreground">Cronograma</Label><Switch id="pay-toggle" checked={showPay} onCheckedChange={togglePay} data-testid="propuesta-pay-toggle" /></div>
          <div className="flex items-center gap-2"><Label htmlFor="iva-toggle" className="text-sm text-muted-foreground">IVA</Label><Switch id="iva-toggle" checked={iva} onCheckedChange={setIva} data-testid="propuesta-iva-toggle" /></div>
          <Button variant="outline" size="sm" className="gap-1.5 h-9" onClick={saveVersion} disabled={saving} data-testid="prop-save-version">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar versión</Button>
          <div className="flex items-center overflow-hidden rounded-md border border-border" data-testid="prop-status-control">
            {[["draft", "Borrador"], ["sent", "Enviada"], ["accepted", "Aceptada"]].map(([val, label]) => {
              const active = (currentMeta?.status || "draft") === val && currentMeta;
              return (
                <button key={val} onClick={() => setStatus(val)} disabled={marking}
                  className={`h-9 px-3 text-xs font-semibold transition-colors ${active ? (val === "accepted" ? "bg-emerald-600 text-white" : val === "sent" ? "bg-sky-600 text-white" : "bg-slate-600 text-white") : "bg-background text-muted-foreground hover:bg-muted"} disabled:opacity-60`}
                  data-testid={`prop-status-${val}`}>{label}</button>
              );
            })}
          </div>
          <Button onClick={downloadPdf} disabled={downloading || loading} className="gap-1.5 h-9" data-testid="propuesta-download">{downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : (
        <div className="mx-auto max-w-[840px] px-4 py-6">
          <p className="mb-3 text-center text-xs text-muted-foreground">Edita cualquier texto o importe directamente sobre el documento. El total se recalcula solo. Pulsa "Guardar versión" para conservarla.</p>
          <div className="rounded-lg border border-border bg-white p-10 shadow-xl text-[#171726] text-[13px]" data-testid="propuesta-doc" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
            <div className="mb-7 flex items-start justify-between">
              {logo ? <img src={logo} alt="Grabarte" className="h-10 w-auto" /> : <span className="text-2xl font-extrabold tracking-wide">GRABARTE</span>}
              <div className="text-right text-[11px] text-slate-500">Presupuesto<br /><input value={header.date} onChange={(e) => upHeader("date", e.target.value)} className={`${inp} text-right`} size={10} data-testid="prop-date" /></div>
            </div>
            <input value={header.project_name} onChange={(e) => upHeader("project_name", e.target.value)} className={`${inp} block w-full text-xl font-extrabold`} data-testid="prop-title" />
            <input value={header.subtitle} onChange={(e) => upHeader("subtitle", e.target.value)} className={`${inp} mt-1 mb-5 block w-full text-slate-500`} data-testid="prop-subtitle" />
            <div className="mb-6 grid grid-cols-2 gap-x-8 gap-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              {[["ID", "id_ref", "#"], ["Proyecto", "project_name", ""], ["Contacto", "contact", ""], ["By", "by", ""], ["Cliente", "client", ""], ["Proveedor", "provider", ""]].map(([label, key, prefix]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="w-[74px] shrink-0 text-slate-500">{label}</span>
                  <span className="flex-1 font-semibold">{prefix}<input value={header[key]} onChange={(e) => upHeader(key, e.target.value)} className={`${inp} w-[calc(100%-8px)]`} data-testid={`prop-info-${key}`} /></span>
                </div>
              ))}
            </div>

            <div className="mb-1.5 mt-6 border-b-2 border-[#26775E] pb-1.5 text-xs font-extrabold uppercase tracking-widest text-[#26775E]">Partidas presupuestarias</div>
            <table className="w-full border-collapse"><tbody>
              {chapters.map((ch, ci) => (
                <ChapterBlock key={ci} ch={ch} ci={ci} inp={inp} chapTotal={chapTotal(ch)} upChapter={upChapter} upSub={upSub} addSub={addSub} delSub={delSub} delChapter={delChapter} />
              ))}
            </tbody></table>
            <button onClick={addChapter} className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#26775E] hover:underline" data-testid="prop-add-chapter"><Plus className="h-3.5 w-3.5" /> Añadir capítulo</button>

            <div className="ml-auto mt-5 w-[300px]">
              {iva && (<>
                <div className="flex justify-between py-1.5 text-[13px]"><span>Base imponible</span><span>{eur(total)}</span></div>
                <div className="flex justify-between py-1.5 text-[13px]"><span>IVA (21%)</span><span>{eur(ivaAmt)}</span></div>
              </>)}
              <div className="mt-1 flex justify-between border-t-2 border-[#171726] pt-3 text-[17px] font-extrabold text-[#26775E]" data-testid="prop-total"><span>TOTAL</span><span>{eur(iva ? total + ivaAmt : total)}</span></div>
            </div>

            {showPay && (
              <div data-testid="prop-pay-section">
                <div className="mb-1.5 mt-8 border-b-2 border-[#26775E] pb-1.5 text-xs font-extrabold uppercase tracking-widest text-[#26775E]">Cronograma de pagos</div>
                <table className="w-full border-collapse"><tbody>
                  {pays.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 align-top">
                      <td className="py-2 pr-2"><input value={r.label} onChange={(e) => upPay(i, "label", e.target.value)} className={`${inp} w-full`} data-testid={`prop-pay-label-${i}`} /></td>
                      <td className="w-[110px] py-2 text-right"><input type="number" value={r.pct} onChange={(e) => upPay(i, "pct", e.target.value)} className={`${inp} w-16 text-right`} data-testid={`prop-pay-pct-${i}`} /> %</td>
                      <td className="w-[130px] whitespace-nowrap py-2 text-right font-semibold">{eur((Number(r.pct) || 0) / 100 * total)}</td>
                      <td className="w-8 py-2 text-right"><button onClick={() => delPay(i)} className="text-slate-300 hover:text-red-500" data-testid={`prop-pay-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody></table>
                <button onClick={addPay} className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#26775E] hover:underline" data-testid="prop-pay-add"><Plus className="h-3.5 w-3.5" /> Añadir línea</button>
                <textarea value={payNote} onChange={(e) => setPayNote(e.target.value)} rows={2} className={`${inp} mt-3 block w-full resize-none italic text-[11px] text-slate-500`} data-testid="prop-pay-note" />
              </div>
            )}

            <div className="mt-9 border-t border-slate-200 pt-3 text-[10px] leading-relaxed text-slate-400">
              {header.provider} · Presupuesto válido durante 30 días desde la fecha de emisión.<br />
              Los importes {iva ? "incluyen" : "no incluyen"} IVA. Este documento tiene carácter de propuesta y no supone contrato hasta su aceptación.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChapterBlock({ ch, ci, inp, chapTotal, upChapter, upSub, addSub, delSub, delChapter }) {
  return (
    <>
      <tr className="border-b-[1.5px] border-slate-300">
        <td className="w-[52px] pt-4 align-top font-extrabold text-[#26775E]">{String(ci + 1).padStart(2, "0")}</td>
        <td className="pt-4 align-top">
          <div className="flex items-center gap-2">
            <input value={ch.name} onChange={(e) => upChapter(ci, "name", e.target.value)} className={`${inp} w-full text-[13px] font-extrabold`} data-testid={`prop-chapter-name-${ci}`} />
            <button onClick={() => delChapter(ci)} className="shrink-0 text-slate-300 hover:text-red-500" title="Eliminar capítulo" data-testid={`prop-chapter-del-${ci}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </td>
        <td className="w-[130px] whitespace-nowrap pt-4 text-right align-top text-[13px] font-extrabold">{eur(chapTotal)}</td>
      </tr>
      {ch.subs.map((s, si) => (
        <tr key={si} className="border-b border-slate-100 text-slate-700">
          <td className="w-[52px] py-2.5 align-top font-semibold text-slate-400">{String(ci + 1).padStart(2, "0")}.{String(si + 1).padStart(2, "0")}</td>
          <td className="py-2.5 align-top">
            <input value={s.name} onChange={(e) => upSub(ci, si, "name", e.target.value)} className={`${inp} w-full`} data-testid={`prop-sub-name-${ci}-${si}`} />
            <textarea value={s.concepts} onChange={(e) => upSub(ci, si, "concepts", e.target.value)} rows={Math.max(1, (s.concepts || "").split("\n").length)} placeholder="Conceptos (uno por línea)…" className={`${inp} mt-1 block w-full resize-none text-[11px] text-slate-500`} data-testid={`prop-sub-concepts-${ci}-${si}`} />
          </td>
          <td className="w-[130px] py-2.5 text-right align-top">
            <div className="flex items-center justify-end gap-1">
              <input type="number" step="0.01" value={s.price} onChange={(e) => upSub(ci, si, "price", e.target.value)} className={`${inp} w-24 text-right tabular-nums`} data-testid={`prop-sub-price-${ci}-${si}`} />
              <span className="text-slate-400">€</span>
              <button onClick={() => delSub(ci, si)} className="text-slate-300 hover:text-red-500" title="Eliminar partida" data-testid={`prop-sub-del-${ci}-${si}`}><Trash2 className="h-3 w-3" /></button>
            </div>
          </td>
        </tr>
      ))}
      <tr><td></td><td colSpan={2} className="pb-2 pt-1"><button onClick={() => addSub(ci)} className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-[#26775E]" data-testid={`prop-add-sub-${ci}`}><Plus className="h-3 w-3" /> Añadir partida</button></td></tr>
    </>
  );
}
